import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

import { NgIcon } from '@ng-icons/core';

import { CurrencyAmountComponent } from '@cxc/ui-kit/components/currency-amount';

import { CURRENCY } from '@cxc/core/enums';

import { API } from '@models/API';

/** One segment of the bar, with its share and its colour. */
interface BreakdownSlice {
  label: string;
  trusts: number;
  /** Null when the group did not report a commitment: shown as "—", never $0. */
  amount: number | null;
  pct: number | null;
  /** Fill class, the same one the legend dot uses. */
  barClass: string;
}

/**
 * How the lender's commitment splits along one cut (LEN-2363).
 *
 * Two questions share this shape because both are parts of one whole: which
 * priority position the money sits in -- the order it is collected in -- and
 * who administers the portfolio. A billion as sole lender is not a billion as
 * a subordinated tranche: same amount, different risk.
 *
 * The colour comes from the group's place in the list, not from its name, so
 * the master servicer cut does not need to know CXC or Linq.
 */
@Component({
  selector: 'commitment-breakdown',
  standalone: true,
  imports: [NgIcon, CurrencyAmountComponent],
  template: `
    @if (slices().length) {
      <div class="card-border flex h-full flex-col p-6">
        <div class="mb-4 flex items-center gap-2">
          <span class="card-grey flex h-10 w-10 items-center justify-center">
            <ng-icon [name]="icon()" class="text-2xl" />
          </span>
          <h4 class="font-bold">{{ title() }}</h4>
        </div>

        <small
          class="mb-3 flex flex-wrap items-baseline gap-1 text-surface-500">
          Total commitment of
          <currency-amount
            [value]="total()!"
            [currency]="currency()"
            layout="inline" />
          {{ subtitle() }}
        </small>

        <!-- One stacked bar: the groups are parts of the same whole. -->
        <!-- role="img" hides its children, so the bar carries the summary. -->
        <div
          class="flex h-8 w-full overflow-hidden rounded-lg"
          role="img"
          [attr.aria-label]="ariaLabel()">
          @for (slice of slices(); track $index) {
            <div
              class="h-full min-w-[2px] first:rounded-l-lg last:rounded-r-lg"
              [class]="slice.barClass"
              [style.width.%]="slice.pct ?? 0"></div>
          }
        </div>

        <div class="mt-4 flex flex-col">
          @for (slice of slices(); track $index) {
            <div class="flex items-center gap-3 py-1.5">
              <span
                class="h-2.5 w-2.5 shrink-0 rounded-full"
                [class]="slice.barClass"></span>
              <span class="grow truncate text-surface-700">
                {{ slice.label }} &middot; {{ slice.trusts }}
                @if (slice.pct !== null) {
                  &middot; {{ slice.pct.toFixed(1) }}%
                }
              </span>
              <span class="shrink-0 font-body font-bold">
                @if (slice.amount !== null) {
                  <currency-amount
                    [value]="slice.amount"
                    [currency]="currency()"
                    [showCode]="false" />
                } @else {
                  &mdash;
                }
              </span>
            </div>
          }

          <div
            class="mt-auto flex items-center gap-3 border-t border-surface-200 pt-3">
            <span class="grow font-bold">Total Commitment</span>
            <span class="shrink-0 font-body font-bold">
              <currency-amount [value]="total()!" [currency]="currency()" />
            </span>
          </div>
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Block so the card's h-full reaches the stretched grid cell.
  host: { class: 'block h-full' },
})
export class CommitmentBreakdown {
  items = input<API.Trusts.Lender.CoreMetrics.BreakdownItem[] | undefined>(
    undefined
  );
  /** The portfolio's commitment, which the groups' shares add up to. */
  total = input<number | null | undefined>(undefined);
  currency = input<CURRENCY>(CURRENCY.MXN);

  title = input.required<string>();
  icon = input<string>('phosphorStack');
  /** Read after the amount: "…by structure and ranking". */
  subtitle = input<string>('');
  /** The group's name; a null key is the trusts that did not report it. */
  label = input<(key: string | null) => string>(key => key ?? 'N/A');

  /**
   * Palette by position, from most to least visual weight. A cut with more
   * groups than colours reuses the last one: two greys side by side beat an
   * invisible segment.
   */
  private static readonly PALETTE = [
    'bg-secondary-700',
    'bg-pink-400',
    'bg-orange-500',
    'bg-sky-500',
    'bg-surface-300',
  ];

  /**
   * Nothing to draw without a commitment: the bar splits the lender's money,
   * and with none reported every share would read 0% (LEN-2577 never imputes
   * one). The server's `share_pct` is used as is, so the legend matches the
   * API figure for figure.
   */
  slices = computed<BreakdownSlice[]>(() => {
    const items = this.items();
    const total = this.total();

    if (!items?.length || total == null || total <= 0) {
      return [];
    }

    const palette = CommitmentBreakdown.PALETTE;
    const label = this.label();

    return items.map((item, index) => ({
      label: label(item.key),
      trusts: item.trust_count,
      amount: item.committed_amount,
      pct: item.share_pct,
      barClass: palette[Math.min(index, palette.length - 1)],
    }));
  });

  /**
   * What a screen reader hears for the bar: "Title: Group 65.5%, …". A group
   * without a share says so after a colon, so the unreported group does not
   * read "Not reported not reported".
   */
  ariaLabel = computed(() => {
    const parts = this.slices().map(slice =>
      slice.pct === null
        ? `${slice.label}: no commitment reported`
        : `${slice.label} ${slice.pct.toFixed(1)}%`
    );

    return `${this.title()}: ${parts.join(', ')}`;
  });
}
